import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Textarea } from "src/components/ui/TextArea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import { Switch } from "src/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/Avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import {
  ArrowLeft,
  Save,
  User,
  Mail,
  Phone,
  GraduationCap,
  Award,
  Clock,
  DollarSign,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Eye,
  Download,
  Trash2,
  Plus,
  X,
  ShieldCheck,
  Briefcase,
  AlertCircle,
  KeyRound,
} from "lucide-react";
import { useToast } from "src/components/ui/UseToast";
import { supabase } from "src/lib/supabaseClient";
import { calculateTutorStatus } from "src/utils/tutorStatusUtils";
import { normalizeTutorEducation } from "src/utils/tutorEducation";
import { parseStorageUrl } from "src/utils/storageUtils";

interface AvailabilitySlot {
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  status: string;
  isNew?: boolean;
}

interface TutorFormData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profilePictureUrl?: string;
  specializations: string[];
  experience: string;
  education: string;
  bio: string;
  hourlyRate: string;
  status: string;
  isLive: boolean;
  certifications: string;
  cvFilePath?: string;
  rating?: number;
  totalSessions?: number;
  totalStudents?: number;
  createdAt?: string;
  updatedAt?: string;
}

const CV_BUCKET = "tutor-uploads";

const DAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const STANDARD_SUBJECTS = [
  "Mathematics",
  "Physics",
  "Chemistry",
  "Biology",
  "Computer Science",
  "Economics",
  "English",
  "History",
  "Psychology",
  "Philosophy",
  "Statistics",
  "Art",
  "Music",
];

const toTextArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item?.name || item?.subject || "")))
      .filter(Boolean);
  }

  if (typeof value !== "string" || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return toTextArray(parsed);
  } catch {
    // Fall back to comma-separated values below.
  }

  return value.split(",").map((item) => item.trim()).filter(Boolean);
};

const formatEducation = (education: unknown): string => {
  if (typeof education === "string") return education;
  if (!Array.isArray(education) || education.length === 0) return "";

  return education
    .map((entry) => {
      if (typeof entry === "string") return entry;

      const degree = entry?.degree || entry?.level;
      const field = entry?.field;
      const institution = entry?.institution;
      const year = entry?.year_of_completion || entry?.year;

      return [degree, field, institution ? `from ${institution}` : "", year ? `(${year})` : ""]
        .filter(Boolean)
        .join(" ");
    })
    .filter(Boolean)
    .join("; ");
};

const calculateExperienceYears = (experience: unknown, totalYears?: unknown): number => {
  const totalYearsNumber = Number(totalYears);
  if (Number.isFinite(totalYearsNumber) && totalYearsNumber > 0) return totalYearsNumber;
  if (typeof experience === "number") return experience;
  if (typeof experience === "string") return Number(experience) || 0;
  if (!Array.isArray(experience)) return 0;

  return experience.reduce((total, entry) => {
    const years = Number(entry?.years || entry?.duration_years || entry?.experience_years || 0);
    return total + (Number.isFinite(years) ? years : 0);
  }, 0);
};

const getStorageFilePath = (cvFilePath: string): { bucket: string; filePath: string } | null => {
  const parsedUrl = parseStorageUrl(cvFilePath);
  if (parsedUrl) return parsedUrl;

  const normalizedPath = cvFilePath.trim().replace(/^\/+/, "");
  if (!normalizedPath) return null;

  if (normalizedPath.startsWith(`${CV_BUCKET}/`)) {
    return {
      bucket: CV_BUCKET,
      filePath: normalizedPath.slice(CV_BUCKET.length + 1),
    };
  }

  return {
    bucket: CV_BUCKET,
    filePath: normalizedPath,
  };
};

const getFileName = (cvFilePath: string): string => {
  try {
    const path = cvFilePath.startsWith("http") ? new URL(cvFilePath).pathname : cvFilePath;
    const fileName = path.split("/").filter(Boolean).pop();
    return fileName ? decodeURIComponent(fileName) : "Tutor CV";
  } catch {
    const fileName = cvFilePath.split("/").filter(Boolean).pop();
    return fileName || "Tutor CV";
  }
};

const getEditableStatus = (verificationStatus: string | null, isLive: boolean | null): string => {
  const calculatedStatus = calculateTutorStatus(verificationStatus, isLive).status;
  if (calculatedStatus === "active") return "active";
  if (["approved", "submitted", "under_review", "rejected"].includes(verificationStatus || "")) {
    return verificationStatus || "submitted";
  }
  return "submitted";
};

const getStatusUpdates = (status: string, isLiveOverride?: boolean): { verification_status: string; is_live: boolean } => {
  switch (status) {
    case "active":
      return { verification_status: "approved", is_live: true };
    case "approved":
      return { verification_status: "approved", is_live: isLiveOverride ?? false };
    case "under_review":
      return { verification_status: "under_review", is_live: false };
    case "rejected":
      return { verification_status: "rejected", is_live: false };
    case "submitted":
    default:
      return { verification_status: "submitted", is_live: false };
  }
};

export const EditTutor: React.FC = () => {
  const navigate = useNavigate();
  const { tutorId: routeTutorId } = useParams<{ tutorId: string }>();
  const [searchParams] = useSearchParams();
  const tutorId = routeTutorId || searchParams.get("id");
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [downloadingCV, setDownloadingCV] = useState(false);

  // Form State
  const [formData, setFormData] = useState<TutorFormData>({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    specializations: [],
    experience: "",
    education: "",
    bio: "",
    hourlyRate: "",
    status: "submitted",
    isLive: false,
    certifications: "",
  });

  // Availability Slots State
  const [availabilitySlots, setAvailabilitySlots] = useState<AvailabilitySlot[]>([]);
  const [deletedSlotIds, setDeletedSlotIds] = useState<string[]>([]);

  // New slot entry state
  const [newSlotDay, setNewSlotDay] = useState<number>(1); // Monday
  const [newSlotStart, setNewSlotStart] = useState<string>("10:00");
  const [newSlotEnd, setNewSlotEnd] = useState<string>("12:00");
  const [newSlotStatus, setNewSlotStatus] = useState<string>("available");

  // Custom subject input state
  const [customSubjectInput, setCustomSubjectInput] = useState("");

  useEffect(() => {
    if (!tutorId) {
      navigate("/admin/tutors/list", { replace: true });
      return;
    }

    fetchTutor(tutorId);
  }, [navigate, tutorId]);

  useEffect(() => {
    if (formData.firstName || formData.lastName) {
      setTitle(`Edit ${formData.firstName} ${formData.lastName}`);
      setDescription("Tutor Account Configuration & Dispatch Settings");
      setActions(undefined);
    }

    return () => reset();
  }, [formData.firstName, formData.lastName, setTitle, setDescription, setActions, reset]);

  const fetchTutor = async (id: string) => {
    try {
      setLoading(true);
      const [tutorRes, sessionsRes, slotsRes] = await Promise.all([
        supabase
          .from("tutors")
          .select(`
            id,
            education,
            hourly_rate,
            subjects_taught,
            experience,
            bio,
            verification_status,
            is_live,
            rating,
            achievements,
            expertise,
            total_years_exp,
            cv_file_path,
            created_at,
            updated_at,
            profile:profiles!tutors_id_fkey (
              first_name,
              last_name,
              email,
              phone,
              profile_picture_url
            )
          `)
          .eq("id", id)
          .maybeSingle(),
        supabase.from("sessions").select("student_id").eq("tutor_id", id),
        supabase
          .from("tutor_availability")
          .select("id, day_of_week, start_time, end_time, status")
          .eq("tutor_id", id)
          .order("day_of_week", { ascending: true })
          .order("start_time", { ascending: true }),
      ]);

      if (tutorRes.error) throw tutorRes.error;
      if (!tutorRes.data) {
        setFormData((prev) => ({ ...prev, id: "" }));
        return;
      }

      const data = tutorRes.data;
      const profile = Array.isArray(data.profile) ? data.profile[0] : data.profile;
      const certifications = [
        ...toTextArray(data.achievements),
        ...toTextArray(data.expertise),
      ].join(", ");

      const totalSessions = sessionsRes.data?.length || 0;
      const totalStudents = new Set((sessionsRes.data || []).map((s: { student_id: string }) => s.student_id).filter(Boolean)).size;

      const currentStatus = getEditableStatus(data.verification_status, data.is_live);
      const isCurrentlyLive = Boolean(data.is_live);

      setFormData({
        id: data.id,
        firstName: profile?.first_name || "",
        lastName: profile?.last_name || "",
        email: profile?.email || "",
        phone: profile?.phone || "",
        profilePictureUrl: profile?.profile_picture_url || "",
        specializations: toTextArray(data.subjects_taught),
        experience: String(calculateExperienceYears(data.experience, data.total_years_exp) || ""),
        education: formatEducation(data.education),
        bio: data.bio || "",
        hourlyRate: data.hourly_rate == null ? "" : String(data.hourly_rate),
        status: currentStatus,
        isLive: isCurrentlyLive,
        certifications,
        cvFilePath: data.cv_file_path || "",
        rating: data.rating != null ? Number(data.rating) : undefined,
        totalSessions,
        totalStudents,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      });

      if (slotsRes.data) {
        setAvailabilitySlots(
          slotsRes.data.map((slot) => ({
            id: slot.id,
            day_of_week: slot.day_of_week,
            start_time: (slot.start_time || "").slice(0, 5),
            end_time: (slot.end_time || "").slice(0, 5),
            status: slot.status || "available",
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch tutor details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch tutor details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TutorFormData, value: string | string[] | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStatusChange = (newStatus: string) => {
    setFormData((prev) => {
      let isLiveVal = prev.isLive;
      if (newStatus === "active") {
        isLiveVal = true;
      } else if (newStatus === "approved") {
        isLiveVal = prev.isLive;
      } else {
        isLiveVal = false;
      }

      return {
        ...prev,
        status: newStatus,
        isLive: isLiveVal,
      };
    });
  };

  const handleLiveToggle = (checked: boolean) => {
    setFormData((prev) => {
      let newStatus = prev.status;
      if (checked) {
        if (newStatus === "approved" || newStatus === "submitted" || newStatus === "under_review") {
          newStatus = "active";
        }
      } else {
        if (newStatus === "active") {
          newStatus = "approved";
        }
      }

      return {
        ...prev,
        isLive: checked,
        status: newStatus,
      };
    });
  };

  const handleSpecializationToggle = (subject: string) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.includes(subject)
        ? prev.specializations.filter((s) => s !== subject)
        : [...prev.specializations, subject],
    }));
  };

  const handleAddCustomSubject = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = customSubjectInput.trim();
    if (!clean) return;

    if (!formData.specializations.includes(clean)) {
      setFormData((prev) => ({
        ...prev,
        specializations: [...prev.specializations, clean],
      }));
    }
    setCustomSubjectInput("");
  };

  const handleCopyUUID = () => {
    if (!tutorId) return;
    navigator.clipboard.writeText(tutorId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
    toast({
      title: "Copied",
      description: "Tutor UUID copied to clipboard.",
    });
  };

  const handleViewCV = async () => {
    if (!formData.cvFilePath) return;
    setDownloadingCV(true);
    try {
      const storageInfo = getStorageFilePath(formData.cvFilePath);
      if (!storageInfo) {
        if (formData.cvFilePath.startsWith("http://") || formData.cvFilePath.startsWith("https://")) {
          window.open(formData.cvFilePath, "_blank", "noopener,noreferrer");
          return;
        }
        throw new Error("Invalid CV storage reference");
      }

      const { data, error } = await supabase.storage
        .from(storageInfo.bucket)
        .createSignedUrl(storageInfo.filePath, 3600);

      if (error || !data?.signedUrl) throw error || new Error("Unable to create signed URL");

      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to open CV:", error);
      toast({
        title: "Error",
        description: "Failed to open tutor CV document.",
        variant: "destructive",
      });
    } finally {
      setDownloadingCV(false);
    }
  };

  const handleAddSlot = () => {
    if (!newSlotStart || !newSlotEnd) {
      toast({
        title: "Invalid Interval",
        description: "Please specify both start and end times.",
        variant: "destructive",
      });
      return;
    }

    if (newSlotStart >= newSlotEnd) {
      toast({
        title: "Invalid Interval",
        description: "Start time must be strictly before end time.",
        variant: "destructive",
      });
      return;
    }

    const newSlot: AvailabilitySlot = {
      day_of_week: newSlotDay,
      start_time: newSlotStart,
      end_time: newSlotEnd,
      status: newSlotStatus,
      isNew: true,
    };

    setAvailabilitySlots((prev) => [...prev, newSlot].sort((a, b) => {
      if (a.day_of_week !== b.day_of_week) return a.day_of_week - b.day_of_week;
      return a.start_time.localeCompare(b.start_time);
    }));

    toast({
      title: "Slot Added",
      description: `Added ${DAY_OPTIONS.find((d) => d.value === newSlotDay)?.label} ${newSlotStart}-${newSlotEnd} (IST).`,
    });
  };

  const handleRemoveSlot = (indexToRemove: number) => {
    const slot = availabilitySlots[indexToRemove];
    if (slot.id) {
      setDeletedSlotIds((prev) => [...prev, slot.id!]);
    }
    setAvailabilitySlots((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!tutorId) return;

    setSaving(true);
    try {
      const statusUpdates = getStatusUpdates(formData.status, formData.isLive);
      const experienceYears = Number(formData.experience) || 0;
      const hourlyRate = formData.hourlyRate ? Number(formData.hourlyRate) : null;
      const certificationsList = toTextArray(formData.certifications);

      // 1. Update Profile & Tutor records
      const [{ error: profileError }, { error: tutorError }] = await Promise.all([
        supabase
          .from("profiles")
          .update({
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", tutorId),
        supabase
          .from("tutors")
          .update({
            education: normalizeTutorEducation(formData.education),
            hourly_rate: hourlyRate,
            subjects_taught: formData.specializations,
            experience: experienceYears ? [{ years: experienceYears }] : [],
            total_years_exp: experienceYears,
            bio: formData.bio.trim(),
            achievements: certificationsList,
            ...statusUpdates,
            updated_at: new Date().toISOString(),
          })
          .eq("id", tutorId),
      ]);

      if (profileError) throw profileError;
      if (tutorError) throw tutorError;

      // 2. Synchronize Availability Slots
      if (deletedSlotIds.length > 0) {
        const { error: deleteError } = await supabase
          .from("tutor_availability")
          .delete()
          .in("id", deletedSlotIds);
        if (deleteError) console.warn("Failed to delete removed slots:", deleteError);
      }

      const newSlotsToInsert = availabilitySlots
        .filter((s) => s.isNew)
        .map((s) => ({
          tutor_id: tutorId,
          day_of_week: s.day_of_week,
          start_time: s.start_time,
          end_time: s.end_time,
          status: s.status || "available",
        }));

      if (newSlotsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from("tutor_availability")
          .insert(newSlotsToInsert);
        if (insertError) console.warn("Failed to insert new slots:", insertError);
      }

      toast({
        title: "Tutor Updated Successfully",
        description: "All profile details, dispatch settings, and availability intervals have been saved.",
      });

      // Refetch to refresh IDs and reset dirty states
      await fetchTutor(tutorId);
      setDeletedSlotIds([]);
    } catch (error) {
      console.error("Failed to update tutor:", error);
      toast({
        title: "Save Failed",
        description: "Failed to update tutor details. Please review inputs and try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-80 space-y-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <p className="text-xs text-slate-500 font-medium">Loading tutor dossier...</p>
      </div>
    );
  }

  if (!formData.id) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-10 text-center max-w-lg mx-auto shadow-2xs">
        <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
        <h2 className="text-base font-bold text-slate-900 mb-1">Tutor Record Not Found</h2>
        <p className="text-xs text-slate-500 mb-5">
          The requested tutor profile does not exist or may have been deleted.
        </p>
        <Button
          onClick={() => navigate("/admin/tutors/list")}
          className="h-9 px-4 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white"
        >
          Return to Tutor Directory
        </Button>
      </div>
    );
  }

  const initials = `${(formData.firstName[0] || "").toUpperCase()}${(formData.lastName[0] || "").toUpperCase()}` || "TU";
  const isFormValid = Boolean(
    formData.firstName.trim() &&
    formData.lastName.trim() &&
    formData.email.trim() &&
    formData.specializations.length > 0
  );

  return (
    <div className="space-y-6">
      {/* Top Breadcrumb & Action Trio Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white border border-slate-200 rounded-xl px-5 py-3.5 shadow-2xs">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/tutors/list")}
            className="h-7 px-2 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1 text-slate-500" />
            Tutor Directory
          </Button>
          <span className="text-slate-300">/</span>
          <button
            onClick={handleCopyUUID}
            className="font-mono text-slate-500 hover:text-purple-700 flex items-center gap-1 transition"
            title="Click to copy full UUID"
          >
            <span>{tutorId?.slice(0, 8)}...{tutorId?.slice(-4)}</span>
            {copiedId ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-slate-400" />}
          </button>
          <span className="text-slate-300">/</span>
          <span className="font-bold text-slate-900">
            {formData.firstName} {formData.lastName}
          </span>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/tutors/view/${tutorId}`)}
            className="h-8 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border-slate-300 shadow-2xs"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/tutors/view/${tutorId}`)}
            className="h-8 text-xs font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 shadow-2xs flex items-center gap-1.5"
          >
            <Eye className="w-3.5 h-3.5" />
            View Dossier
          </Button>
          <Button
            type="button"
            onClick={() => handleSubmit()}
            disabled={saving || !isFormValid}
            className="h-8 px-4 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-xs shadow-purple-300 flex items-center gap-1.5"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-1" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main Command Hub Grid: Left 4-Col Fixed Context Rail + Right 8-Col Flush Workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* ========================================================================= */}
        {/* LEFT 4-COL: Fixed Profile & Operational Governance Rail                   */}
        {/* ========================================================================= */}
        <div className="lg:col-span-4 space-y-5">
          
          {/* Identity & Status Card */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
            <div className="bg-[#faf9fe] border-b border-slate-200 p-5">
              <div className="flex items-start gap-4">
                <div className="relative shrink-0">
                  <Avatar className="w-14 h-14 rounded-2xl border-2 border-purple-200 text-purple-800 font-bold text-lg shadow-xs bg-purple-100">
                    {formData.profilePictureUrl && (
                      <AvatarImage src={formData.profilePictureUrl} alt={formData.firstName} />
                    )}
                    <AvatarFallback className="rounded-2xl bg-purple-100 text-purple-800 font-bold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  {formData.isLive && (
                    <span
                      className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white shadow-xs"
                      title="Live & Dispatchable"
                    />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-bold text-slate-900 truncate flex items-center gap-1.5">
                    <span>{formData.firstName} {formData.lastName}</span>
                    {formData.status === "active" && (
                      <ShieldCheck className="w-4 h-4 text-purple-600 shrink-0" />
                    )}
                  </h2>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{formData.email}</p>
                  
                  <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                    {formData.isLive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active & Live
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                        Off-air Standby
                      </span>
                    )}

                    {formData.hourlyRate && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-purple-50 text-purple-700 border border-purple-200">
                        ${Number(formData.hourlyRate).toFixed(2)}/hr
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Operational Governance Controls */}
            <div className="p-5 space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1.5">
                  Verification Lifecycle State
                </label>
                <Select value={formData.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-full h-9 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-800">
                    <SelectValue placeholder="Select lifecycle status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active (Approved & Live)</SelectItem>
                    <SelectItem value="approved">Approved (Standby / Off-air)</SelectItem>
                    <SelectItem value="under_review">Under Review (Background Check Pending)</SelectItem>
                    <SelectItem value="submitted">Submitted (New Application)</SelectItem>
                    <SelectItem value="rejected">Rejected (Does Not Meet Standard)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-slate-500 text-xs">
                  Governs tutor visibility in student matchmaking catalogs.
                </p>
              </div>

              {/* Live Dispatch Toggle Switch */}
              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="pr-3">
                  <div className="font-semibold text-slate-800">Dispatch Eligibility</div>
                  <div className="text-slate-500 text-xs">Enable in automated session matchmaking</div>
                </div>
                <Switch
                  checked={formData.isLive}
                  onCheckedChange={handleLiveToggle}
                />
              </div>

              {/* Performance Metrics Trio */}
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-center">
                <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs text-slate-500 font-medium">Rating</div>
                  <div className="text-sm font-bold text-slate-900 mt-0.5">
                    {formData.rating != null ? `${formData.rating.toFixed(2)} ★` : "4.9 ★"}
                  </div>
                </div>
                <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs text-slate-500 font-medium">Completed</div>
                  <div className="text-sm font-bold text-slate-900 mt-0.5 font-mono">
                    {formData.totalSessions || 0} ses
                  </div>
                </div>
                <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs text-slate-500 font-medium">Students</div>
                  <div className="text-sm font-bold text-slate-900 mt-0.5 font-mono">
                    {formData.totalStudents || 0} act
                  </div>
                </div>
              </div>

              {/* CV Document Link & System Telemetry */}
              <div className="pt-3 border-t border-slate-100 space-y-2 text-slate-600">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-medium">Curriculum Vitae:</span>
                  {formData.cvFilePath ? (
                    <button
                      type="button"
                      onClick={handleViewCV}
                      disabled={downloadingCV}
                      className="text-purple-700 hover:text-purple-800 font-semibold flex items-center gap-1 text-xs truncate max-w-[160px]"
                      title="View uploaded CV document"
                    >
                      <FileText className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{getFileName(formData.cvFilePath)}</span>
                    </button>
                  ) : (
                    <span className="text-slate-400 italic text-xs">Not uploaded</span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-medium">System Timezone:</span>
                  <span className="font-mono text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded text-xs">
                    Asia/Kolkata (IST)
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-medium">Record UUID:</span>
                  <button
                    type="button"
                    onClick={handleCopyUUID}
                    className="font-mono text-xs text-slate-600 hover:text-purple-700 flex items-center gap-1"
                  >
                    <span>{tutorId?.slice(0, 8)}...</span>
                    <Copy className="w-3 h-3 text-slate-400" />
                  </button>
                </div>
              </div>

            </div>
          </div>

          {/* Quick Utility Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs text-xs space-y-3">
            <h4 className="font-bold text-slate-900">Administrative Tools</h4>
            <p className="text-slate-500">
              Access the complete audit record or review previous onboarding submissions.
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/tutors/view/${tutorId}`)}
                className="flex-1 h-8 text-xs font-semibold bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"
              >
                Open Full Dossier
              </Button>
            </div>
          </div>

        </div>

        {/* ========================================================================= */}
        {/* RIGHT 8-COL: Flush Tabbed Workspaces                                      */}
        {/* ========================================================================= */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
          
          <Tabs defaultValue="personal" className="w-full">
            {/* Pixel-Perfect Flush Tabs per DESIGN.md §4.1 */}
            <div className="border-b border-slate-200 bg-slate-50 px-6">
              <TabsList className="h-auto p-0 bg-transparent flex items-center gap-1 rounded-none justify-start overflow-x-auto w-full">
                <TabsTrigger
                  value="personal"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Personal & Contact
                </TabsTrigger>
                
                <TabsTrigger
                  value="subjects"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none flex items-center gap-1.5"
                >
                  <span>Subjects & Curriculum</span>
                  <span className="px-1.5 py-0.2 rounded bg-purple-100 text-purple-700 text-xs font-mono font-semibold">
                    {formData.specializations.length}
                  </span>
                </TabsTrigger>

                <TabsTrigger
                  value="qualifications"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Qualifications & Bio
                </TabsTrigger>

                <TabsTrigger
                  value="availability"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none flex items-center gap-1.5"
                >
                  <span>Weekly Availability</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* TAB 1: Personal & Contact */}
            <TabsContent value="personal" className="p-6 space-y-6 mt-0">
              <div>
                <div className="pb-3 border-b border-slate-100 mb-4">
                  <h3 className="text-sm font-bold text-slate-900">Legal Identity & Contact Coordinates</h3>
                  <p className="text-xs text-slate-500">Official profile information reflected on session rosters, receipts, and communication bridge.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      First Name <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange("firstName", e.target.value)}
                        className="pl-9 h-9 text-xs"
                        placeholder="Legal first name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Last Name <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange("lastName", e.target.value)}
                        className="pl-9 h-9 text-xs"
                        placeholder="Legal last name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Email Address <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className="pl-9 h-9 text-xs"
                        placeholder="tutor@domain.org"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Phone Number (International format)
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        className="pl-9 h-9 text-xs"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Compensation & Tenure Sub-section */}
              <div className="pt-2">
                <div className="pb-3 border-b border-slate-100 mb-4">
                  <h3 className="text-sm font-bold text-slate-900">Compensation & Experience Metrics</h3>
                  <p className="text-xs text-slate-500">Hourly billing rate in USD and confirmed post-qualification teaching experience.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Standard Hourly Rate ($ USD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-xs font-semibold text-slate-400">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.hourlyRate}
                        onChange={(e) => handleInputChange("hourlyRate", e.target.value)}
                        className="pl-7 h-9 text-xs font-mono font-medium"
                        placeholder="65.00"
                      />
                    </div>
                    <span className="text-xs text-slate-500 mt-1 block">
                      Default hourly billing rate applied to 1-on-1 objective bookings.
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Total Teaching Experience (Years)
                    </label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        value={formData.experience}
                        onChange={(e) => handleInputChange("experience", e.target.value)}
                        className="h-9 text-xs font-mono font-medium pr-14"
                        placeholder="8"
                      />
                      <span className="absolute right-3 top-2.5 text-xs text-slate-400">years</span>
                    </div>
                    <span className="text-xs text-slate-500 mt-1 block">
                      Cumulative confirmed tenure across academic institutions or tutoring.
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 flex justify-end">
                <Button
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={saving || !isFormValid}
                  className="h-9 px-5 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Save Personal Information
                </Button>
              </div>
            </TabsContent>

            {/* TAB 2: Subjects & Curriculum */}
            <TabsContent value="subjects" className="p-6 space-y-6 mt-0">
              <div>
                <div className="pb-3 border-b border-slate-100 mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Curricular Scopes & Subject Authorizations</h3>
                    <p className="text-xs text-slate-500">
                      Disciplines in which this tutor is approved to teach, diagnose, and assess curriculum objectives.
                    </p>
                  </div>
                  <span className="text-xs font-mono text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded">
                    {formData.specializations.length} active
                  </span>
                </div>

                {/* Active Subject Tags */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    Active Subject Authorizations
                  </label>
                  {formData.specializations.length === 0 ? (
                    <div className="p-4 border border-dashed border-amber-300 bg-amber-50/50 rounded-lg text-xs text-amber-800">
                      No subjects currently authorized. Select or add at least one subject below.
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {formData.specializations.map((subject) => (
                        <span
                          key={subject}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-50 text-purple-900 border border-purple-200 shadow-2xs"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-600 shrink-0" />
                          <span>{subject}</span>
                          <button
                            type="button"
                            onClick={() => handleSpecializationToggle(subject)}
                            className="ml-1 text-purple-400 hover:text-purple-700 focus:outline-none"
                            title={`Remove ${subject}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Standard Subjects Quick-Toggle Grid */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                  <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5">
                    Standard Curriculum Disciplines
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {STANDARD_SUBJECTS.map((subject) => {
                      const isSelected = formData.specializations.includes(subject);
                      return (
                        <button
                          key={subject}
                          type="button"
                          onClick={() => handleSpecializationToggle(subject)}
                          className={`text-xs px-2.5 py-1 rounded-md font-medium transition border ${
                            isSelected
                              ? "bg-purple-600 text-white border-purple-600 shadow-2xs"
                              : "bg-white hover:bg-purple-50 text-slate-700 hover:text-purple-700 border-slate-200"
                          }`}
                        >
                          {isSelected ? `✓ ${subject}` : `+ ${subject}`}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Custom Subject Addition Field */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Add Custom Specialized Subject
                  </label>
                  <form onSubmit={handleAddCustomSubject} className="flex gap-2 max-w-md">
                    <Input
                      type="text"
                      value={customSubjectInput}
                      onChange={(e) => setCustomSubjectInput(e.target.value)}
                      placeholder="e.g. AP Chemistry, Linear Algebra, SAT Verbal"
                      className="h-9 text-xs"
                    />
                    <Button
                      type="submit"
                      variant="outline"
                      size="sm"
                      className="h-9 px-3 text-xs font-semibold bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 shrink-0"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add Scope
                    </Button>
                  </form>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 flex justify-end">
                <Button
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={saving || !isFormValid}
                  className="h-9 px-5 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Save Subject Scopes
                </Button>
              </div>
            </TabsContent>

            {/* TAB 3: Qualifications & Bio */}
            <TabsContent value="qualifications" className="p-6 space-y-6 mt-0">
              <div>
                <div className="pb-3 border-b border-slate-100 mb-4">
                  <h3 className="text-sm font-bold text-slate-900">Academic Background & Credentials</h3>
                  <p className="text-xs text-slate-500">
                    Degrees, universities, certified achievements, and public teaching philosophy.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                      <GraduationCap className="w-4 h-4 text-slate-500" />
                      Education Background
                    </label>
                    <Textarea
                      value={formData.education}
                      onChange={(e) => handleInputChange("education", e.target.value)}
                      placeholder="e.g. Ph.D. in Physics from Oxford University (2020); B.Sc. in Mathematics from Imperial College (2016)"
                      rows={3}
                      className="text-xs leading-relaxed"
                    />
                    <span className="text-xs text-slate-500 mt-1 block">
                      Include degree title, field of study, alma mater, and graduation year.
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                      <Award className="w-4 h-4 text-slate-500" />
                      Certifications & Accreditations
                    </label>
                    <Textarea
                      value={formData.certifications}
                      onChange={(e) => handleInputChange("certifications", e.target.value)}
                      placeholder="e.g. Cambridge International Teacher Certificate, IBDP Physics Certified Examiner"
                      rows={3}
                      className="text-xs leading-relaxed"
                    />
                    <span className="text-xs text-slate-500 mt-1 block">
                      Comma-separated accredited milestones or teaching certifications.
                    </span>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-slate-700">
                        Public Bio & Teaching Philosophy
                      </label>
                      <span className="text-xs text-slate-400 font-mono">
                        {formData.bio.length} characters
                      </span>
                    </div>
                    <Textarea
                      value={formData.bio}
                      onChange={(e) => handleInputChange("bio", e.target.value)}
                      placeholder="Detailed introduction and pedagogy statement shown on public student matching cards..."
                      rows={5}
                      className="text-xs leading-relaxed"
                    />
                    <span className="text-xs text-slate-500 mt-1 block">
                      Markdown syntax is supported. Rendered prominently on student-facing booking profiles.
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 flex justify-end">
                <Button
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={saving || !isFormValid}
                  className="h-9 px-5 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Save Qualifications & Bio
                </Button>
              </div>
            </TabsContent>

            {/* TAB 4: Weekly Availability */}
            <TabsContent value="availability" className="p-6 space-y-6 mt-0">
              <div>
                <div className="pb-3 border-b border-slate-100 mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Weekly Availability Schedule Matrix</h3>
                    <p className="text-xs text-slate-500">
                      Standard recurring operating windows for lesson scheduling and automated dispatch.
                    </p>
                  </div>
                  <span className="text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded px-2.5 py-1">
                    Timezone: <strong className="font-mono">Asia/Kolkata (IST, UTC+05:30)</strong>
                  </span>
                </div>

                {/* Slots Table */}
                <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-semibold">
                      <tr>
                        <th className="py-2.5 px-4">Day of Week</th>
                        <th className="py-2.5 px-4">Start Time (IST)</th>
                        <th className="py-2.5 px-4">End Time (IST)</th>
                        <th className="py-2.5 px-4">Dispatch Mode</th>
                        <th className="py-2.5 px-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                      {availabilitySlots.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-6 text-center text-slate-400 italic">
                            No recurring weekly slots configured. Add your first time slot below.
                          </td>
                        </tr>
                      ) : (
                        availabilitySlots.map((slot, index) => {
                          const dayLabel = DAY_OPTIONS.find((d) => d.value === slot.day_of_week)?.label || `Day ${slot.day_of_week}`;
                          return (
                            <tr key={`${slot.day_of_week}-${slot.start_time}-${index}`} className="hover:bg-slate-50/60">
                              <td className="py-2.5 px-4 font-bold text-slate-900">{dayLabel}</td>
                              <td className="py-2.5 px-4 font-mono text-slate-800">{slot.start_time}</td>
                              <td className="py-2.5 px-4 font-mono text-slate-800">{slot.end_time}</td>
                              <td className="py-2.5 px-4">
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                  slot.status === "auto_accept"
                                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                                    : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                }`}>
                                  {slot.status === "auto_accept" ? "Auto Accept" : "Available"}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-right">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSlot(index)}
                                  className="text-rose-600 hover:text-rose-800 font-semibold inline-flex items-center gap-1"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Add Slot Row */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
                    Add Recurring Availability Block
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Day of Week</label>
                      <Select
                        value={String(newSlotDay)}
                        onValueChange={(val) => setNewSlotDay(Number(val))}
                      >
                        <SelectTrigger className="h-9 bg-white text-xs">
                          <SelectValue placeholder="Select day" />
                        </SelectTrigger>
                        <SelectContent>
                          {DAY_OPTIONS.map((d) => (
                            <SelectItem key={d.value} value={String(d.value)}>
                              {d.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Start Time (IST)</label>
                      <Input
                        type="time"
                        value={newSlotStart}
                        onChange={(e) => setNewSlotStart(e.target.value)}
                        className="h-9 text-xs font-mono bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">End Time (IST)</label>
                      <Input
                        type="time"
                        value={newSlotEnd}
                        onChange={(e) => setNewSlotEnd(e.target.value)}
                        className="h-9 text-xs font-mono bg-white"
                      />
                    </div>

                    <div>
                      <Button
                        type="button"
                        onClick={handleAddSlot}
                        className="w-full h-9 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add Block
                      </Button>
                    </div>
                  </div>
                </div>

              </div>

              <div className="pt-4 border-t border-slate-200 flex justify-end">
                <Button
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={saving || !isFormValid}
                  className="h-9 px-5 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Save Availability Matrix
                </Button>
              </div>
            </TabsContent>

          </Tabs>

        </div>

      </div>
    </div>
  );
};

export default EditTutor;
